import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { StatusBadge } from '../components/StatusBadge';
import {
  EnhancedTable,
  StickyTableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
} from '../components/ui/enhanced-table';
import { 
  Shield, 
  Clock, 
  CheckCircle2, 
  AlertTriangle, 
  TrendingUp, 
  ArrowRight,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return '—';
  
  const absMinutes = Math.abs(Math.round(minutes));
  const sign = minutes < 0 ? '-' : '';
  
  if (absMinutes >= 1440) {
    const days = Math.floor(absMinutes / 1440);
    const hours = Math.floor((absMinutes % 1440) / 60);
    return hours > 0 ? `${sign}${days}d ${hours}h` : `${sign}${days}d`;
  }
  if (absMinutes >= 60) {
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return mins > 0 ? `${sign}${hours}h ${mins}m` : `${sign}${hours}h`;
  }
  return `${sign}${absMinutes}m`;
}

export default function SLACompliancePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRTL = i18n.language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [breachedWOs, setBreachedWOs] = useState([]);
  
  // Filters
  const [filterBranch, setFilterBranch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (filterBranch) params.branch_id = filterBranch;
      if (filterType) params.wo_type = filterType;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;
      
      const [branchesRes, summaryRes, breachedRes] = await Promise.all([
        api.get('/branches'),
        api.get('/sla-policies/compliance/summary', { params }),
        api.get('/sla-policies/compliance/breached', { params: { ...params, include_near_breach: true } }),
      ]);
      
      setBranches(branchesRes.data);
      setSummary(summaryRes.data);
      setBreachedWOs(breachedRes.data);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterType, filterStartDate, filterEndDate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTriggerScan = async () => {
    try {
      const res = await api.post('/notifications/sla-breach-scan');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const getBreachBadge = (sla) => {
    if (sla.is_resolution_breached) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
          <AlertTriangle className="w-3 h-3 me-1" />
          {t('sla.resolutionBreached')}
        </Badge>
      );
    }
    if (sla.is_response_breached) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
          <AlertTriangle className="w-3 h-3 me-1" />
          {t('sla.responseBreached')}
        </Badge>
      );
    }
    if (sla.is_resolution_near_breach || sla.is_response_near_breach) {
      return (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
          <Clock className="w-3 h-3 me-1" />
          {t('sla.nearBreach')}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="sla-compliance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-brand" />
            {t('sla.compliance')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t('sla.complianceSubtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleTriggerScan} data-testid="trigger-sla-scan-btn">
            <RefreshCw className="w-4 h-4 me-2" />
            Scan Now
          </Button>
          <Button onClick={() => navigate('/sla-policies')} data-testid="manage-policies-btn">
            <Shield className="w-4 h-4 me-2" />
            {t('sla.title')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('common.branch')}</Label>
              <Select value={filterBranch || 'all'} onValueChange={(v) => setFilterBranch(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]" data-testid="compliance-branch-filter">
                  <SelectValue placeholder={t('common.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('common.type')}</Label>
              <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]" data-testid="compliance-type-filter">
                  <SelectValue placeholder={t('common.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="corrective">{t('sla.corrective')}</SelectItem>
                  <SelectItem value="preventive">{t('sla.preventive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('common.from')}</Label>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="w-[150px]"
                data-testid="compliance-start-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('common.to')}</Label>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="w-[150px]"
                data-testid="compliance-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Response SLA Met */}
          <Card data-testid="kpi-response-met">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('sla.responseMet')}</p>
                  <p className="text-3xl font-bold mt-1">
                    {summary.response_met_pct}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {summary.response_met_count} / {summary.response_met_count + summary.response_breached_count}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${summary.response_met_pct >= 90 ? 'bg-green-500/10' : summary.response_met_pct >= 70 ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
                  <Clock className={`w-5 h-5 ${summary.response_met_pct >= 90 ? 'text-green-500' : summary.response_met_pct >= 70 ? 'text-amber-500' : 'text-red-500'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resolution SLA Met */}
          <Card data-testid="kpi-resolution-met">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('sla.resolutionMet')}</p>
                  <p className="text-3xl font-bold mt-1">
                    {summary.resolution_met_pct}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {summary.resolution_met_count} / {summary.resolution_met_count + summary.resolution_breached_count}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${summary.resolution_met_pct >= 90 ? 'bg-green-500/10' : summary.resolution_met_pct >= 70 ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
                  <CheckCircle2 className={`w-5 h-5 ${summary.resolution_met_pct >= 90 ? 'text-green-500' : summary.resolution_met_pct >= 70 ? 'text-amber-500' : 'text-red-500'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avg Response Time */}
          <Card data-testid="kpi-avg-response">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('sla.avgResponseTime')}</p>
                  <p className="text-3xl font-bold mt-1">
                    {formatDuration(summary.avg_response_minutes)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {summary.with_sla} {t('workOrder.workOrders').toLowerCase()}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avg Resolution Time */}
          <Card data-testid="kpi-avg-resolution">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('sla.avgResolutionTime')}</p>
                  <p className="text-3xl font-bold mt-1">
                    {formatDuration(summary.avg_resolution_minutes)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {summary.total_wos} {t('common.total').toLowerCase()}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Breached Work Orders Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            {t('sla.breachedWorkOrders')}
          </CardTitle>
          <CardDescription>
            {breachedWOs.length} {t('workOrder.workOrders').toLowerCase()} {t('sla.breached').toLowerCase()} {t('common.or').toLowerCase()} {t('sla.nearBreach').toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <EnhancedTable>
            <StickyTableHeader>
              <tr className="border-b border-border">
                <TableHeader>ID</TableHeader>
                <TableHeader>{t('common.status')}</TableHeader>
                <TableHeader>SLA</TableHeader>
                <TableHeader>{t('common.branch')}</TableHeader>
                <TableHeader>{t('workOrder.room')}</TableHeader>
                <TableHeader>{t('workOrder.assignedTo')}</TableHeader>
                <TableHeader>{t('sla.overdueBy')}</TableHeader>
                <TableHeader className="text-end">{t('common.actions')}</TableHeader>
              </tr>
            </StickyTableHeader>
            <EnhancedTableBody>
              {breachedWOs.length === 0 ? (
                <EnhancedTableRow>
                  <EnhancedTableCell colSpan={8}>
                    <TableEmptyState
                      icon={CheckCircle2}
                      title={t('sla.noBreaches')}
                      description="All work orders are within SLA targets"
                    />
                  </EnhancedTableCell>
                </EnhancedTableRow>
              ) : (
                breachedWOs.map(item => {
                  const remaining = item.sla.is_resolution_breached || item.sla.is_resolution_near_breach
                    ? item.sla.resolution_remaining_minutes
                    : item.sla.response_remaining_minutes;
                  
                  return (
                    <EnhancedTableRow 
                      key={item.wo_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/work-orders/${item.wo_id}`)}
                      data-testid={`breached-wo-row-${item.wo_id}`}
                    >
                      <EnhancedTableCell className="font-mono text-xs">{item.wo_id_short}</EnhancedTableCell>
                      <EnhancedTableCell><StatusBadge status={item.status} /></EnhancedTableCell>
                      <EnhancedTableCell>{getBreachBadge(item.sla)}</EnhancedTableCell>
                      <EnhancedTableCell>{item.branch_name}</EnhancedTableCell>
                      <EnhancedTableCell className="text-muted-foreground">{item.room_name || '—'}</EnhancedTableCell>
                      <EnhancedTableCell className="text-muted-foreground">{item.assigned_to_name || t('workOrder.unassigned')}</EnhancedTableCell>
                      <EnhancedTableCell>
                        <span className={remaining < 0 ? 'text-red-600 font-medium' : 'text-amber-600'}>
                          {formatDuration(remaining)}
                        </span>
                      </EnhancedTableCell>
                      <EnhancedTableCell className="text-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/work-orders/${item.wo_id}`);
                          }}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </EnhancedTableCell>
                    </EnhancedTableRow>
                  );
                })
              )}
            </EnhancedTableBody>
          </EnhancedTable>
        </CardContent>
      </Card>
    </div>
  );
}
